import { Controller } from "@hotwired/stimulus"

// The design preview's submit form: three steps that open one at a time (any can be opened), each ticked
// once its required fields are filled, and the listing's card redrawn by the server as the form changes.
export default class extends Controller {
  static targets = ["form", "step", "summary", "card", "image", "imageName", "barTitle", "sheet"]
  static values = { previewUrl: String, week: String, city: String, image: String, imported: Boolean }

  connect() {
    this.imageUrl = null
    // An imported form is filled in throughout, so every step can show its tick
    this.visited = new Set(this.importedValue ? this.stepTargets.map((step) => step.dataset.step) : ["basics"])
    this.refreshSteps()
    this.paintImage()
  }

  disconnect() {
    clearTimeout(this.timer)
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl)
  }

  toggle(event) {
    const step = event.currentTarget.closest("[data-step]")
    this.open(step, !step.hasAttribute("data-open"))
  }

  continue(event) {
    const step = event.currentTarget.closest("[data-step]")
    const index = this.stepTargets.indexOf(step)
    if (!this.complete(step) && step.querySelector("[data-required]")) {
      this.firstMissing(step)?.focus()
      return
    }
    this.open(step, false)
    const next = this.stepTargets[index + 1]
    if (next) {
      this.open(next, true)
      next.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  open(step, opening) {
    if (opening) {
      this.stepTargets.forEach((other) => other !== step && this.setOpen(other, false))
      this.visited.add(step.dataset.step)
    }
    this.setOpen(step, opening)
    this.refreshSteps()
  }

  setOpen(step, open) {
    step.toggleAttribute("data-open", open)
    step.querySelector(".v2-step-head").setAttribute("aria-expanded", String(open))
  }

  changed() {
    this.refreshSteps()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refreshCard(), 250)
  }

  imageChosen() {
    const file = this.imageTarget.files[0]
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl)
    this.imageUrl = file ? URL.createObjectURL(file) : null
    this.imageNameTarget.textContent = file ? file.name : "Add a picture"
    this.paintImage()
  }

  openSheet() {
    this.sheetTarget.showModal()
  }

  closeSheet() {
    this.sheetTarget.close()
  }

  closeSheetOutside(event) {
    if (event.target === this.sheetTarget) this.closeSheet()
  }

  // A step is done once its required fields are filled; steps without any, once they've been opened and left
  complete(step) {
    const required = [...step.querySelectorAll("[data-required]")]
    if (required.length === 0) return this.visited.has(step.dataset.step) && !step.hasAttribute("data-open")
    return required.every((field) => field.value.trim() !== "")
  }

  firstMissing(step) {
    const field = [...step.querySelectorAll("[data-required]")].find((input) => input.value.trim() === "")
    return field?.type === "hidden" ? field.closest(".relative")?.querySelector("button") : field
  }

  refreshSteps() {
    this.stepTargets.forEach((step) => step.toggleAttribute("data-done", this.complete(step)))
    const value = (name) => this.formTarget.querySelector(`[name="event[${name}]"]`)?.value?.trim()
    const select = this.formTarget.querySelector('[name="event[event_type]"]')
    const summaries = {
      basics: [value("title"), select?.value && select.selectedOptions[0]?.text, this.formTarget.querySelector("[data-date-range-target=label]")?.textContent].filter(Boolean).join(" · "),
      details: [value("description") && "Description", (this.imageTarget?.files?.length || this.imageValue) && "Image", (value("external_url") || value("rsvp_url")) && "Links"].filter(Boolean).join(" · "),
      organiser: [value("organiser_name"), value("submission_email")].filter(Boolean).join(" · ")
    }
    this.summaryTargets.forEach((summary) => {
      const text = summaries[summary.dataset.step]
      summary.dataset.hint ||= summary.textContent
      summary.textContent = text && !summary.closest("[data-step]").hasAttribute("data-open") ? text : summary.dataset.hint
    })
    if (this.hasBarTitleTarget) this.barTitleTarget.textContent = value("title") || "Your event title"
  }

  async refreshCard() {
    const data = new FormData(this.formTarget)
    data.delete("event[featured_image]")
    data.delete("_method")
    if (this.weekValue) data.set("week", this.weekValue)
    data.set("city", this.cityValue)
    const token = document.querySelector("meta[name=csrf-token]")?.content
    const response = await fetch(this.previewUrlValue, {
      method: "POST", body: data, headers: { "X-CSRF-Token": token || "", Accept: "text/html" }, credentials: "same-origin"
    }).catch(() => null)
    if (!response?.ok) return

    const html = await response.text()
    this.cardTargets.forEach((card) => { card.innerHTML = html })
    this.paintImage()
  }

  // The picture someone has chosen shows on the card straight away, before it's uploaded; until then, the
  // one ARTSVP has for an imported event
  paintImage() {
    const source = this.imageUrl || this.imageValue
    if (!source) return
    this.cardTargets.forEach((card) => {
      const frame = card.querySelector("article > div:first-child")
      if (!frame) return
      frame.querySelector(".event-type-wash")?.remove()
      let image = frame.querySelector("img")
      if (!image) {
        image = document.createElement("img")
        image.alt = ""
        image.className = "size-full object-cover"
        image.referrerPolicy = "no-referrer"
        frame.prepend(image)
      }
      image.src = source
    })
  }
}
